'use client'
import React, { useState, useEffect, useMemo } from "react";
import { TextField, Card, CardContent, CardHeader, Checkbox, FormControl, FormControlLabel, InputLabel, Input, InputAdornment, IconButton, MenuItem, FormHelperText} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import Select from '@mui/material/Select';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { VisibilityOff, Visibility } from "@mui/icons-material";
import { GCInfoErrors, GCInfoModel } from "@/app/Models/GCInfoModel";
import { CardSiteDataModel } from "@/app/Models/CardSiteDataModel";
import { shuffleBrands } from "@/app/Models/BrandDataModel";
import PinRequired from "../Enums/PinRequired";
import Processor from "../Enums/Processor";
import { normalizeCardNumber } from "@/app/Utilities/cardNumber";
import { formatCents } from "@/app/Utilities/money";

interface Props {
    data: GCInfoModel,
    setData: (model: GCInfoModel) => void;
    cardSiteData: CardSiteDataModel | null;
    errors: GCInfoErrors;
}

// One definition for both money inputs. Module scope on purpose: declaring it inside
// GCInfoCard's render would remount the input every keystroke and drop focus. No onBlur,
// deliberately — reformatting on blur is what surprised users. type="text" not "number":
// a number input reads back "" for partial values and exposes no usable selection API.
// inputMode must go in the NESTED inputProps or MUI drops it on InputBase's root div.
export const MoneyField: React.FC<{
    id: string;
    label: string;
    helperText: string;
    value: string;
    error: boolean;
    onValueChange: (next: string) => void;
}> = ({ id, label, helperText, value, error, onValueChange }) => (
    <TextField
        required
        variant="standard"
        type="text"
        autoComplete="off"
        id={id}
        label={label}
        helperText={helperText}
        value={value}
        error={error}
        onChange={(e) => onValueChange(formatCents(e.target.value))}
        InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            inputProps: { inputMode: 'decimal' },
        }}
    />
);

const GCInfoCard: React.FC<Props> = (props) => {
    const [showGCNumber, setShowGCNumber] = useState(false);
    const [showGCPin, setShowGCPIn] = useState(false);
    const [issuerName, setIssuerName] = useState("");
    const [displayGCPin, setDisplayGCPin] = useState<PinRequired>(PinRequired.REQUIRED);
    const [noPinChecked, setNoPinChecked] = useState<boolean>(false);

    const handleClickShowPassword = (control: String) => 
    { 
        if(control === 'number') setShowGCNumber((show) => !show);
        else if (control === 'pin') setShowGCPIn((show) => !show);
    }

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    };

    useEffect(() => {
        if (props.cardSiteData?.issuer) {
            setIssuerName(props.cardSiteData.issuer);
            props.setData({...props.data, issuerName: props.cardSiteData.issuer});
        }

        if (props.cardSiteData?.pinRequired != null) {
            setDisplayGCPin(props.cardSiteData.pinRequired as PinRequired);
        }
    }, [props.cardSiteData]);

    const processorCode: string = props.cardSiteData?.processorCode ?? "";
const brands: readonly string[] = useMemo(
    () => shuffleBrands(props.cardSiteData?.brands),
    [props.cardSiteData?.cardSiteKey, props.cardSiteData?.brands]
);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Card sx={{ minWidth: 275}} variant="outlined">
                <CardHeader title='Gift Card Information' 
                    avatar={
                        <CreditCardIcon />
                    }/>
                <CardContent>                                                 
                    <FormControl sx={{ m: 1, width: { xs: '100%', sm: '40ch' } }} variant="standard">
                        <InputLabel htmlFor="gc-number-input">Gift Card Number</InputLabel>
                        <Input id="gc-number-input"
                            value={props.data.giftCardNumber}
                            onChange={(e) => props.setData({...props.data, giftCardNumber: normalizeCardNumber(e.target.value)})}
                            error={props.errors.giftCardNumberError}
                            type="text"
                            autoComplete="off"
                            aria-describedby="gc-number-helper-text"
                            inputProps={{
                                style: { WebkitTextSecurity: showGCNumber ? 'none' : 'disc' } as React.CSSProperties,
                            }}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => handleClickShowPassword('number')}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end">
                                        {showGCNumber ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            } />
                        <FormHelperText id="gc-number-helper-text">
                            No spaces or dashes.
                        </FormHelperText>
                    </FormControl>

                    {displayGCPin === PinRequired.CARDHOLDER &&
                    <FormControlLabel
                        sx={{ alignItems: 'center', m: 1 }}
                        control={<Checkbox checked={noPinChecked} onChange={(e) => {
                            setNoPinChecked(e.target.checked);
                            props.setData({...props.data, noPinChecked: e.target.checked, giftCardPin: e.target.checked ? "" : props.data.giftCardPin});
                        }} />}
                        label="I do not have a PIN"
                        labelPlacement="end"
                        componentsProps={{ typography: { sx: { fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)', whiteSpace: 'normal', maxWidth: '60px', textAlign: 'left' } } }}
                    />
                    }

                    {displayGCPin !== PinRequired.NOT_REQUIRED &&
                    <FormControl sx={{ m: 1, width: { xs: '100%', sm: '40ch' } }} variant="standard">
                        <InputLabel htmlFor="gc-pin-input">Gift Card Pin</InputLabel>
                        <Input id="gc-pin-input"
                            value={props.data.giftCardPin}
                            onChange={(e) => props.setData({...props.data, giftCardPin: e.target.value})}
                            error={props.errors.giftCardPinError}
                            disabled={noPinChecked}
                            type="text"
                            autoComplete="off"
                            inputProps={{
                                style: { WebkitTextSecurity: showGCPin ? 'none' : 'disc' } as React.CSSProperties,
                            }}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => handleClickShowPassword('pin')}
                                        onMouseDown={handleMouseDownPassword}
                                        edge="end">
                                        {showGCPin ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            } />
                    </FormControl>
                    }

                    <TextField
                        required
                        variant="standard"
                        value={issuerName}
                        error={props.errors.issuerNameError}
                        id="gc-issuer-textfield"
                        helperText="The name of the company that issued the gift card. This should be located on the back of the card."
                        label="Issuer Name" />
                    
                    <FormControl sx={{ m: 1, width: { xs: '100%', sm: '40ch' } }}>
                        <InputLabel htmlFor="gc-brand-input">Brand Name</InputLabel>
                        <Select 
                            labelId="gc-brand-select-label"
                            id="gc-brand-select"
                            value={props.data.brandName || ""}
                            label="Brand Name"
                            onChange={(e) => props.setData({...props.data, brandName: e.target.value})}>
                                {brands.map((brand) => (
                                    <MenuItem key={brand} value={brand}>
                                        {brand}
                                    </MenuItem>
                                ))}
                        </Select>
                        <FormHelperText>Select the brand (or one brand if multiple) that the gift card is for.</FormHelperText>
                    </FormControl>

                    <MoneyField
                        id="gc-amount-textfield"
                        label="Remaining Balance"
                        helperText="Enter the remaining balance on the gift card."
                        value={props.data.cashOutAmount}
                        error={props.errors.cashOutAmountError}
                        onValueChange={(next) => props.setData({...props.data, cashOutAmount: next})} />

                    {processorCode === Processor.Manual &&
                    <DatePicker
                        views={['year', 'month']}
                        openTo="year"
                        format="YYYY/MM"
                        value={props.data.cardPurchasedDate}
                        onChange={(date) => props.setData({...props.data, cardPurchasedDate: date})}
                        label="Card Purchase Date"
                        slotProps={{ textField: { error: props.errors.cardPurchasedDateError } }}
                    />
                    }

                    {processorCode === Processor.Manual &&
                    <MoneyField
                        id="gc-beginning-card-balance"
                        label="Beginning Card Balance"
                        helperText="Enter the initial amount loaded to the gift card."
                        value={props.data.beginningCardBalance}
                        error={props.errors.beginningCardBalanceError}
                        onValueChange={(next) => props.setData({...props.data, beginningCardBalance: next})} />
                    }
                </CardContent>
            </Card>
        </LocalizationProvider>
        
    );
};

export default GCInfoCard;